import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, MessageSquareWarning, RefreshCcw, Trash2, Loader2 } from "lucide-react";
import api, { getUser, errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import ProfilePreview from "@/components/ProfilePreview";
import { PROFILE_STATUS_STYLES } from "@/lib/constants";
import { Textarea } from "@/components/ui/textarea";

function EditableField({ label, value, onSave, multiline, testid }) {
    const [text, setText] = useState(value || "");
    const [dirty, setDirty] = useState(false);
    useEffect(() => {
        setText(value || "");
        setDirty(false);
    }, [value]);
    const save = async () => {
        await onSave(text);
        setDirty(false);
    };
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50 mb-1">
                {label}
            </div>
            {multiline ? (
                <Textarea
                    rows={4}
                    value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        setDirty(true);
                    }}
                    data-testid={testid}
                />
            ) : (
                <input
                    className="w-full h-9 px-3 rounded-md border border-[#123B5D]/20"
                    value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        setDirty(true);
                    }}
                    data-testid={testid}
                />
            )}
            {dirty ? (
                <button className="mt-1 text-xs text-[#0F766E] font-semibold hover:underline" onClick={save} data-testid={`${testid}-save`}>
                    Save
                </button>
            ) : null}
        </div>
    );
}

export default function AdminReview() {
    const { doctorId } = useParams();
    const nav = useNavigate();
    const user = getUser();
    const [bundle, setBundle] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showChanges, setShowChanges] = useState(false);
    const [showReject, setShowReject] = useState(false);
    const [changesComment, setChangesComment] = useState("");
    const [rejectReason, setRejectReason] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/admin/profiles/${doctorId}`);
            setBundle(data);
        } catch (err) {
            toast.error(errMsg(err, "Failed to load"));
        }
    }, [doctorId]);

    useEffect(() => {
        load();
    }, [load]);

    const doctor = bundle?.doctor;
    const profile = bundle?.profile;
    const items = bundle?.items_by_kind || {};
    const aiJob = bundle?.ai_job;
    const output = bundle?.ai_output?.output || {};
    const history = bundle?.history || [];
    const status = doctor?.profile_status;
    const st = PROFILE_STATUS_STYLES[status];
    const canAct = status === "admin_review";
    const canEdit = status === "admin_review" || status === "changes_requested";

    const saveField = async (field_path, value) => {
        try {
            const { data } = await api.post(`/admin/profiles/${doctorId}/edit`, {
                field_path,
                value,
            });
            setBundle((b) => ({ ...b, ai_output: { ...b.ai_output, output: data.output } }));
            toast.success("Saved");
        } catch (err) {
            toast.error(errMsg(err, "Save failed"));
        }
    };

    const approve = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/profiles/${doctorId}/approve`);
            toast.success("Approved — sent to doctor for final review.");
            await load();
        } catch (err) {
            toast.error(errMsg(err, "Approval failed"));
        } finally {
            setBusy(false);
        }
    };
    const requestChanges = async () => {
        if (!changesComment.trim()) {
            toast.error("Comment required");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/admin/profiles/${doctorId}/request-changes`, {
                comment: changesComment,
            });
            toast.success("Changes requested.");
            setChangesComment("");
            setShowChanges(false);
            await load();
        } catch (err) {
            toast.error(errMsg(err, "Failed"));
        } finally {
            setBusy(false);
        }
    };
    const reject = async () => {
        if (!rejectReason.trim()) {
            toast.error("Reason required");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/admin/profiles/${doctorId}/reject`, { reason: rejectReason });
            toast.success("Rejected.");
            setRejectReason("");
            setShowReject(false);
            await load();
        } catch (err) {
            toast.error(errMsg(err, "Failed"));
        } finally {
            setBusy(false);
        }
    };
    const restore = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/profiles/${doctorId}/restore`);
            toast.success("Restored to admin review.");
            await load();
        } catch (err) {
            toast.error(errMsg(err, "Failed"));
        } finally {
            setBusy(false);
        }
    };
    const retryAi = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/profiles/${doctorId}/retry-ai`);
            toast.success("AI job re-queued.");
            setTimeout(load, 4000);
        } catch (err) {
            toast.error(errMsg(err, "Retry failed"));
        } finally {
            setBusy(false);
        }
    };

    if (!bundle) {
        return (
            <AdminShell>
                <div className="text-[#17212B]/50">Loading…</div>
            </AdminShell>
        );
    }

    return (
        <AdminShell>
            <div className="animate-fade-up">
                <button
                    className="text-xs text-[#0F766E] font-semibold inline-flex items-center gap-1 mb-4"
                    onClick={() => nav("/admin/queue")}
                    data-testid="admin-review-back"
                >
                    <ArrowLeft size={12} /> Back to queue
                </button>
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">
                            Doctor review
                        </div>
                        <h1 className="mt-1 text-3xl font-bold text-[#123B5D]">
                            {doctor?.full_name}
                            <span className="text-sm text-[#17212B]/50 font-normal ml-2">
                                · {doctor?.specialization}
                            </span>
                        </h1>
                        <div className="mt-2 flex items-center gap-2">
                            <span className={"ddi-badge " + (st?.cls || "")} data-testid="admin-review-status">
                                {st?.label || status}
                            </span>
                            <span className="ddi-badge bg-[#123B5D]/5 text-[#123B5D]">
                                {doctor?.package_code || "no package"}
                            </span>
                            <span className="ddi-badge bg-[#123B5D]/5 text-[#123B5D] capitalize">
                                {(doctor?.payment_state || "unpaid").replaceAll("_", " ")}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {canAct ? (
                            <>
                                <button
                                    className="ddi-teal-btn text-sm inline-flex items-center gap-1 disabled:opacity-60"
                                    onClick={approve}
                                    disabled={busy}
                                    data-testid="admin-approve-btn"
                                >
                                    {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                                    Approve
                                </button>
                                <button
                                    className="ddi-outline-btn text-sm inline-flex items-center gap-1"
                                    onClick={() => setShowChanges((v) => !v)}
                                    data-testid="admin-request-changes-toggle"
                                >
                                    <MessageSquareWarning size={14} /> Request changes
                                </button>
                                <button
                                    className="text-sm text-[#DC2626] font-semibold inline-flex items-center gap-1"
                                    onClick={() => setShowReject((v) => !v)}
                                    data-testid="admin-reject-toggle"
                                >
                                    <XCircle size={14} /> Reject
                                </button>
                            </>
                        ) : null}
                        {status === "archived" && user?.role === "super_admin" ? (
                            <button className="ddi-outline-btn text-sm inline-flex items-center gap-1" onClick={restore} disabled={busy} data-testid="admin-restore-btn">
                                <RefreshCcw size={14} /> Restore
                            </button>
                        ) : null}
                        {aiJob?.status === "failed" || (aiJob?.status === "completed" && (aiJob?.warnings || []).length > 5) ? (
                            <button className="ddi-outline-btn text-sm inline-flex items-center gap-1" onClick={retryAi} disabled={busy} data-testid="admin-retry-ai">
                                <RefreshCcw size={14} /> Retry AI
                            </button>
                        ) : null}
                    </div>
                </div>

                {showChanges ? (
                    <div className="mt-4 ddi-card p-4 max-w-3xl" data-testid="admin-changes-panel">
                        <div className="text-sm font-semibold text-[#D97706]">
                            Comment for the doctor
                        </div>
                        <Textarea
                            rows={4}
                            value={changesComment}
                            onChange={(e) => setChangesComment(e.target.value)}
                            className="mt-2"
                            data-testid="admin-changes-comment"
                        />
                        <div className="mt-2 flex gap-2">
                            <button className="ddi-teal-btn text-sm" onClick={requestChanges} disabled={busy} data-testid="admin-changes-submit">
                                Send
                            </button>
                            <button className="text-sm text-[#17212B]/60" onClick={() => setShowChanges(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : null}
                {showReject ? (
                    <div className="mt-4 ddi-card p-4 max-w-3xl" data-testid="admin-reject-panel">
                        <div className="text-sm font-semibold text-[#DC2626]">
                            Reason for rejection
                        </div>
                        <Textarea
                            rows={4}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            className="mt-2"
                            data-testid="admin-reject-reason"
                        />
                        <div className="mt-2 flex gap-2">
                            <button className="ddi-teal-btn text-sm" onClick={reject} disabled={busy} data-testid="admin-reject-submit">
                                Reject
                            </button>
                            <button className="text-sm text-[#17212B]/60" onClick={() => setShowReject(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : null}

                {/* Split view: LEFT input | CENTER AI editable | RIGHT preview */}
                <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-4">
                    {/* LEFT — raw input */}
                    <div className="ddi-card p-5 max-h-[80vh] overflow-y-auto" data-testid="admin-left-input">
                        <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50 font-semibold mb-2">
                            Doctor input
                        </div>
                        <RawInput profile={profile} items={items} doctor={doctor} />
                    </div>
                    {/* CENTER — AI editable */}
                    <div className="ddi-card p-5 max-h-[80vh] overflow-y-auto space-y-4" data-testid="admin-center-editor">
                        <div className="text-[10px] uppercase tracking-widest text-[#0F766E] font-semibold">
                            AI-refined (editable)
                        </div>
                        {!canEdit ? (
                            <div className="text-xs text-[#17212B]/50 italic">
                                Read-only in status “{status?.replaceAll("_", " ")}”.
                            </div>
                        ) : null}
                        {aiJob?.status !== "completed" ? (
                            <div className="text-sm text-[#17212B]/60">
                                AI status:{" "}
                                <span className="font-mono">{aiJob?.status || "—"}</span>
                                {aiJob?.error ? (
                                    <div className="mt-1 text-[#DC2626] text-xs">{aiJob.error}</div>
                                ) : null}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {[
                                    ["professional_biography", "Professional biography", true],
                                    ["profile_summary", "Profile summary", true],
                                    ["specialization_description", "Specialization description", true],
                                    ["practice_philosophy", "Practice philosophy", true],
                                    ["mission_vision", "Mission & vision", true],
                                    ["seo_title", "SEO title", false],
                                    ["meta_description", "Meta description", true],
                                ].map(([k, label, ml]) =>
                                    canEdit ? (
                                        <EditableField
                                            key={k}
                                            label={label}
                                            value={output[k]}
                                            onSave={(v) => saveField(k, v)}
                                            multiline={ml}
                                            testid={`admin-edit-${k}`}
                                        />
                                    ) : (
                                        <div key={k}>
                                            <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50 mb-1">
                                                {label}
                                            </div>
                                            <div className="text-sm text-[#17212B]/85 whitespace-pre-line">{output[k]}</div>
                                        </div>
                                    )
                                )}
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50 mb-1">
                                        Career timeline
                                    </div>
                                    <ul className="text-sm space-y-1">
                                        {(output.career_timeline || []).map((t, i) => (
                                            <li key={i} className="font-mono text-[#17212B]/85">
                                                {t.year} — {t.event}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50 mb-1">
                                        FAQs
                                    </div>
                                    <ul className="text-sm space-y-2">
                                        {(output.faqs || []).map((f, i) => (
                                            <li key={i}>
                                                <div className="font-semibold text-[#123B5D]">{f.q}</div>
                                                <div className="text-[#17212B]/80">{f.a}</div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                {(bundle?.ai_output?.warnings || []).length ? (
                                    <div className="mt-2 text-[11px] text-[#D97706]">
                                        AI guard warnings: {bundle.ai_output.warnings.length}
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                    {/* RIGHT — preview */}
                    <div className="min-w-0" data-testid="admin-right-preview">
                        <ProfilePreview doctor={doctor} profile={profile} output={output} viewport="desktop" />
                    </div>
                </div>

                {/* History */}
                <div className="mt-8">
                    <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">
                        Approval history
                    </div>
                    <div className="mt-2 ddi-card p-4" data-testid="admin-history">
                        {history.length === 0 ? (
                            <div className="text-xs text-[#17212B]/50">No history yet.</div>
                        ) : (
                            <ul className="text-sm divide-y divide-[#123B5D]/5">
                                {history.map((h) => (
                                    <li key={h.id} className="py-2 flex items-start justify-between gap-3">
                                        <div>
                                            <div className="font-semibold text-[#123B5D] capitalize">
                                                {h.action?.replaceAll("_", " ")}{" "}
                                                <span className="text-xs text-[#17212B]/50 font-normal">
                                                    ({h.from_status} → {h.to_status})
                                                </span>
                                            </div>
                                            {h.comment ? (
                                                <div className="text-xs text-[#17212B]/70 mt-0.5">
                                                    {h.comment}
                                                </div>
                                            ) : null}
                                        </div>
                                        <div className="text-[10px] text-[#17212B]/40 shrink-0">
                                            {new Date(h.created_at).toLocaleString()}{" "}
                                            <span className="capitalize">· {h.actor_role}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </AdminShell>
    );
}

function RawInput({ profile, items, doctor }) {
    const p = profile || {};
    const sec = (title, body) => (
        <div className="mb-4">
            <div className="text-[10px] uppercase tracking-widest text-[#0F766E] mb-1 font-semibold">
                {title}
            </div>
            <div className="text-xs text-[#17212B]/85 whitespace-pre-line">{body}</div>
        </div>
    );
    const list = (title, arr, fmt) => (
        <div className="mb-4">
            <div className="text-[10px] uppercase tracking-widest text-[#0F766E] mb-1 font-semibold">
                {title} ({(arr || []).length})
            </div>
            <ul className="text-xs text-[#17212B]/85 space-y-1">
                {(arr || []).map((i) => (
                    <li key={i.id}>· {fmt(i.payload || {})}</li>
                ))}
            </ul>
        </div>
    );
    return (
        <div>
            {sec("Doctor", `${doctor?.full_name} · ${doctor?.specialization} · ${doctor?.city || "—"} · Reg ${doctor?.medical_registration_number || "—"}`)}
            {sec("Personal", `Gender: ${p.gender || "—"} · DOB: ${p.date_of_birth || "—"} · Languages: ${(p.languages || []).join(", ") || "—"}`)}
            {sec("Professional", `${p.registration_council || "—"} · ${p.registration_year || "—"} · ${p.years_of_experience ?? "—"} yrs · Qual: ${(p.primary_qualifications || []).join(", ") || "—"}`)}
            {sec("Specialization", `${p.primary_specialization || "—"} · Sub: ${(p.sub_specializations || []).join(", ") || "—"} · Interests: ${(p.areas_of_interest || []).join(", ") || "—"} · Procedures: ${(p.procedures_offered || []).join(", ") || "—"}`)}
            {list("Education", items.education, (i) => `${i.degree}, ${i.institution}${i.year ? " · " + i.year : ""}`)}
            {list("Experience", items.experience, (i) => `${i.role} at ${i.organization}${i.from_year ? " · " + i.from_year : ""}${i.to_year ? "–" + i.to_year : ""}`)}
            {list("Clinics", items.clinic, (i) => `${i.clinic_name || "—"} · ${i.city || "—"}`)}
            {list("Hospital affiliations", items.hospital_affiliation, (i) => `${i.hospital_name || "—"}${i.role ? " · " + i.role : ""}`)}
            {sec("Consultation", `${(p.consultation_hours || []).length} slots · Base ₹${p.base_fee_inr ?? "—"} · Types: ${(p.consultation_types || []).join(", ") || "—"}`)}
            {list("Achievements", items.achievement, (i) => `${i.title || i.name || "—"}${i.year ? " · " + i.year : ""}`)}
            {list("Awards", items.award, (i) => `${i.title || "—"}${i.awarding_body ? " · " + i.awarding_body : ""}${i.year ? " · " + i.year : ""}`)}
            {list("Publications", items.publication, (i) => `${i.title || "—"}${i.journal ? " · " + i.journal : ""}`)}
            {list("Memberships", items.membership, (i) => `${i.organization || "—"}${i.position ? " · " + i.position : ""}`)}
            {list("Social", items.social_link, (i) => `${i.platform || "—"} · ${i.url || ""}`)}
            {sec("Patient info", `Expect: ${p.patient_info?.what_to_expect || "—"} · Bring: ${p.patient_info?.what_to_bring || "—"}`)}
            {sec("Biography notes", p.biography_notes || "—")}
            {sec("SEO hints", `Keywords: ${(p.seo_keywords || []).join(", ") || "—"} · Tagline: ${p.seo_tagline || "—"}`)}
        </div>
    );
}
